import {
  Button,
  Icon,
  IconButton,
  IconNames,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import * as React from 'react';

import { useBreakpointValue } from 'utils/mediaQuery';

import styles from './header.module.scss';

type HeaderProps = {
  children: React.ReactNode;
  iconsList: React.ReactNode;
  title?: string;
  className?: string;
  isOpen: boolean;
  isFavorite: boolean;
  avatar: React.ReactNode;
  small?: boolean;
  canCall?: boolean;
  onCallClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onEditClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onFavoriteClick: () => void;
  onToggle: (e: React.MouseEvent<HTMLElement>, isOpen: boolean) => void;
  status: string;
  onPhone?: boolean;
  simultaneous?: boolean;
  actions?: React.ReactElement;
};

export const Header = ({
  children,
  className,
  iconsList,
  title,
  isOpen,
  isFavorite,
  avatar,
  small,
  canCall,
  onCallClick,
  onEditClick,
  onFavoriteClick,
  onToggle,
  status,
  onPhone,
  simultaneous,
  actions,
  ...otherProps
}: HeaderProps): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, lg: false });

  const rootClass = classnames(
    {
      [styles.headerTeam]: true,
      [styles.headerTeamOpen]: isOpen,
    },
    className,
  );

  const avatarClasses = classnames(
    {
      [styles.avatarWrap]: true,
      [styles.avatarSmall]: small,
    },
    className,
  );

  const iconsClass = classnames(
    {
      [styles.icons]: canCall !== true,
      [styles.iconsCall]: canCall === true,
    },
    className,
  );

  const statusClass = classnames(
    {
      [styles.status]: !onPhone && !simultaneous,
      [styles.statusActive]: status === 'working',
      [styles.statusAway]: status === 'away',
      [styles.statusOffline]: status === 'offline',
    },
    className,
  );

  const iconChannelClass = classnames(
    {
      [styles.statusIcon]: onPhone || simultaneous,
      [styles.statusIconActive]: status === 'working',
      [styles.statusIconAway]: status === 'away',
      [styles.statusIconOffline]: status === 'offline',
    },
    className,
  );

  let iconChannel = '';
  if (onPhone === true) {
    iconChannel = 'phone';
  } else if (simultaneous === true) {
    iconChannel = 'simultaneous';
  }

  const handleFavoriteToggle = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    onFavoriteClick?.();
  };

  return (
    <div
      {...otherProps}
      className={rootClass}
      onClick={(e) => onToggle(e, !isOpen)}
      // prevent selection of contents if doubleclicking
      onMouseDown={(event) => event.preventDefault()}
    >
      <div className={styles.headerPerson}>
        {isFavorite !== undefined && (
          <IconButton
            className={styles.favoriteIcon}
            variant="tertiary"
            icon={isFavorite ? 'star-full' : 'star'}
            iconProps={{
              color: isFavorite ? colors.orange[300] : colors.neutral[400],
            }}
            onClick={handleFavoriteToggle}
          />
        )}
        {!!avatar && <div className={avatarClasses}>{avatar}</div>}
        {(status === 'away' || status === 'working' || status === 'offline') &&
          !onPhone &&
          !simultaneous && <div className={statusClass} />}
        {(onPhone || simultaneous) && (
          <Icon name={iconChannel as IconNames} className={iconChannelClass} />
        )}
        <Text.Paragraph
          as="div"
          variant="2"
          onMouseDown={(event) => event.stopPropagation()}
          className={styles.title}
        >
          {children}
        </Text.Paragraph>
      </div>

      <div className={styles.pullRight}>
        {!isSmallScreen || !iconsList ? (
          <div className={iconsClass}>{iconsList}</div>
        ) : null}

        {!!canCall && (
          <Button
            variant="secondary"
            className={styles.teamCardButton}
            onClick={onCallClick}
          >
            Call
          </Button>
        )}
        {onEditClick && (
          <Button
            variant="secondary"
            className={styles.teamCardButton}
            onClick={onEditClick}
          >
            Edit
          </Button>
        )}
        {actions}

        <Icon
          name="angle-right"
          color="secondary"
          size={24}
          className={styles.arrow}
        />
      </div>
    </div>
  );
};

Header.displayName = 'Header';
